import { Link, useParams } from '@tanstack/react-router';
import { ArrowLeft, Loader2, ExternalLink, XCircle, CheckCircle2 } from 'lucide-react';
import { cn } from '@jeraisonne/ui';
import {
  useYoutubeVideo,
  type SocialPublicationRow,
  type YoutubeJobRow,
} from '@/lib/admin/youtube-queries';
import { useCancelYoutubeJob } from '@/lib/admin/youtube-mutations';
import {
  VIDEO_STATUS_LABEL,
  VIDEO_STATUS_CLS,
  videoIsProcessing,
  jobStepLabel,
  JOB_STEP_PROGRESS,
  PLATFORM_LABEL,
  PRIVACY_LABEL,
} from '@/lib/admin/youtube-labels';
import { VideoEditor } from '@/features/youtube-studio/video-editor';

export function TutorAdminYoutubeVideoPage() {
  const { videoId } = useParams({ strict: false }) as { videoId: string };
  const { data, isLoading } = useYoutubeVideo(videoId, { live: true });

  if (isLoading) {
    return (
      <div className="mx-auto max-w-5xl p-6">
        <div className="bg-ink-100 h-40 animate-pulse rounded-2xl" />
      </div>
    );
  }
  if (!data) {
    return (
      <div className="mx-auto max-w-5xl p-6">
        <p className="text-ink-500">Vidéo introuvable.</p>
      </div>
    );
  }

  const { video, job, publications } = data;
  const processing = videoIsProcessing(video.status);

  return (
    <div className="mx-auto max-w-5xl p-4 sm:p-6">
      <Link
        to="/tutor/admin/youtube"
        className="text-ink-500 hover:text-ink-900 mb-4 inline-flex items-center gap-1.5 font-sans text-[13px] transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        YouTube Studio
      </Link>

      <div className="mb-5 flex flex-wrap items-center gap-2">
        <span
          className={cn(
            'font-display inline-flex h-6 items-center rounded-full border px-2.5 text-[11px] font-bold',
            VIDEO_STATUS_CLS[video.status],
          )}
        >
          {VIDEO_STATUS_LABEL[video.status]}
        </span>
        <h1 className="font-display text-ink-900 text-xl font-extrabold">
          {video.exercise?.title ?? 'Exercice'}
        </h1>
      </div>

      {/* Statut du job de rendu */}
      {(processing || job) && <JobStatus videoId={videoId} job={job} status={video.status} />}

      {/* Publications */}
      {publications.length > 0 && (
        <div className="mb-5 space-y-2">
          {publications.map((p) => (
            <PublicationRow key={p.id} pub={p} />
          ))}
        </div>
      )}

      {/* Éditeur (ré-édition tant que non publiée) */}
      <VideoEditor initial={video} />
    </div>
  );
}

function JobStatus({
  videoId,
  job,
  status,
}: {
  videoId: string;
  job: YoutubeJobRow | null;
  status: string;
}) {
  const cancel = useCancelYoutubeJob();
  const active = job && (job.status === 'queued' || job.status === 'processing');
  const done = status === 'published' || job?.status === 'done';
  const failed = status === 'failed' || job?.status === 'failed' || job?.status === 'abandoned';
  const progress = done
    ? 100
    : job?.current_step
      ? JOB_STEP_PROGRESS[job.current_step]
      : status === 'queued'
        ? 5
        : 0;

  return (
    <div className="border-ink-200 mb-5 rounded-2xl border bg-white p-4">
      <div className="flex items-center justify-between">
        <span
          className={cn(
            'font-display inline-flex items-center gap-2 text-sm font-bold',
            done ? 'text-emerald-700' : failed ? 'text-accent-600' : 'text-ink-700',
          )}
        >
          {active && <Loader2 className="text-primary-500 h-4 w-4 animate-spin" />}
          {done && <CheckCircle2 className="h-4 w-4" />}
          {failed && <XCircle className="h-4 w-4" />}
          {done
            ? 'Vidéo générée ✅'
            : failed
              ? 'Échec du rendu'
              : (jobStepLabel(job?.current_step) ??
                (status === 'queued' ? 'En attente d’un worker…' : 'Rendu'))}
        </span>
        {active && job && (
          <button
            type="button"
            onClick={() => cancel.mutate({ videoId, jobId: job.id })}
            className="text-accent-600 inline-flex items-center gap-1 text-xs font-bold"
          >
            <XCircle className="h-3.5 w-3.5" /> Annuler
          </button>
        )}
      </div>
      {progress > 0 && !failed && (
        <div className="bg-ink-100 mt-3 h-2 w-full overflow-hidden rounded-full">
          <div
            className={cn(
              'h-full rounded-full transition-all',
              done ? 'bg-emerald-500' : 'bg-primary-500',
            )}
            style={{ width: `${progress}%` }}
          />
        </div>
      )}
      {failed && job?.error && (
        <p className="text-accent-600 mt-2 font-sans text-xs">{job.error}</p>
      )}
    </div>
  );
}

function PublicationRow({ pub }: { pub: SocialPublicationRow }) {
  return (
    <div className="border-ink-200 flex items-center gap-3 rounded-xl border bg-white p-3">
      <span className="font-display text-ink-900 text-sm font-bold">
        {PLATFORM_LABEL[pub.platform]}
      </span>
      <span className="text-ink-500 text-xs">{PRIVACY_LABEL[pub.privacy]}</span>
      {pub.scheduled_at && (
        <span className="text-ink-400 text-xs">
          ⏰ {new Date(pub.scheduled_at).toLocaleString('fr-FR')}
        </span>
      )}
      <div className="ml-auto">
        {pub.external_url ? (
          <a
            href={pub.external_url}
            target="_blank"
            rel="noreferrer"
            className="text-primary-600 inline-flex items-center gap-1 text-xs font-bold"
          >
            <ExternalLink className="h-3.5 w-3.5" /> Voir
          </a>
        ) : (
          <span className="text-ink-400 text-xs">{VIDEO_STATUS_LABEL[pub.status]}</span>
        )}
      </div>
    </div>
  );
}
